import React from 'react';

import { useClaimRewardsMutation, useGetRewardsQuery } from '../../store/services/rewardsApi';
import { claimRewardsOnce, totalRewardCredits } from '../rewardClaims';
import { useRewardClaimBatch } from '../useRewardClaimBatch';

import { ActionButtonLabel } from './ActionBarActions';
import { actionInvocationOrigin } from './actionInvocationOrigin';
import { fireConfetti } from './confetti';

/** Rewards remain actionable after the conversation moves to another planning step. */
export const useUnclaimedRewardActions = ({ projectId, sessionId, isReadOnly, hidden }: {
  projectId?: string | null; sessionId?: string | null; isReadOnly?: boolean; hidden?: boolean;
}): React.ReactNode[] => {
  const enabled = Boolean(projectId && !isReadOnly && !hidden);
  const { currentData = [] } = useGetRewardsQuery(projectId!, { skip: !enabled });
  const scope = JSON.stringify([projectId, sessionId]);
  const [claim] = useClaimRewardsMutation({ fixedCacheKey: `reward-batch:${scope}` });
  const batch = useRewardClaimBatch(scope);
  const offers = Array.from(new Map(currentData.filter((offer) => (
    offer.project_id === projectId && offer.can_claim && !offer.claimed_at
    && (!sessionId || !offer.session_id || offer.session_id === sessionId)
  )).map((offer) => [offer.id, offer])).values());

  if (!enabled || !offers.length) return [];

  const amount = totalRewardCredits(offers);
  const label = batch.busy ? 'Claiming…' : `${batch.error ? 'Retry claim' : 'Claim'} ${amount} credits`;

  return [<React.Fragment key={scope}>
    <button type="button" className="ai-overview-action-btn ai-overview-action-btn--claim"
      data-action-placement="pinned" disabled={batch.busy} aria-busy={batch.busy || undefined}
      title={offers.length > 1 ? `Claim ${amount} credits from ${offers.length} rewards`
        : offers[0].reason || offers[0].original_text}
      onClick={(event) => {
        const origin = actionInvocationOrigin(event.currentTarget);

        // The displayed offers go out as one request: one grant, one receipt, one burst of confetti.
        void batch.run(() => claimRewardsOnce(claim, offers, sessionId, () => fireConfetti(origin)));
      }}>
      <ActionButtonLabel label={label} />
    </button>
    {batch.error && <span className="ai-overview-action-error" data-action-layout-message="true" role="alert">
      {batch.error}
    </span>}
  </React.Fragment>];
};
